/**
 * Gated marketing copy when gift cards are offered (PBI #990).
 *
 * Landing carousel uses {@link MailedCardGiftCardFeatureBadge} on tiles instead
 * of inline subtitle text beside the section heading.
 *
 * Public shop pages may pass {@link reserveSpace} so the hero does not grow
 * after the async gift-card config resolves (PBI #1135).
 */

import { Box, Text, rem } from '@mantine/core';
import { landingContent } from '../../content/landingContent';

export type MailedCardGiftCardSellingCopyVariant = 'subline' | 'helper';

/** Reserved height for one `size="sm"` centered subline under the shop hero. */
const GIFT_CARD_SUBLINE_RESERVE_MIN_HEIGHT = rem(40);

export interface MailedCardGiftCardSellingCopyProps {
  /** When false, renders nothing unless {@link reserveSpace} is true. */
  visible: boolean;
  variant?: MailedCardGiftCardSellingCopyVariant;
  /**
   * When true, always occupies the subline slot so async `visible` flips do not
   * shift the catalog grid. Hidden when not visible (no screen-reader copy).
   */
  reserveSpace?: boolean;
}

/**
 * Shows mailed-card gift-card selling copy only when the API reports offered amounts.
 */
export function MailedCardGiftCardSellingCopy({
  visible,
  variant = 'subline',
  reserveSpace = false,
}: MailedCardGiftCardSellingCopyProps) {
  const copy = landingContent.mailedCardGiftCard;

  if (variant === 'helper') {
    if (!visible) {
      return null;
    }

    return (
      <Text size="sm" c="dimmed" ta="center">
        {copy.previewHelper}
      </Text>
    );
  }

  if (!visible && !reserveSpace) {
    return null;
  }

  return (
    <Box
      mt="xs"
      mih={reserveSpace ? GIFT_CARD_SUBLINE_RESERVE_MIN_HEIGHT : undefined}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {visible ? (
        <Text size="sm" c="dimmed" ta="center">
          {copy.shopSubline}
        </Text>
      ) : null}
    </Box>
  );
}
